import { h } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import { route } from 'preact-router';
import { api } from '../state/auth.js';
import { notifications } from '../state/notifications.js';
import { translateError } from '../i18n.js';
import Card from '../components/shared/Card.jsx';
import Button from '../components/shared/Button.jsx';
import AiAssist from '../components/shared/AiAssist.jsx';
import PhotoLink from '../components/shared/PhotoLink.jsx';
import { shrinkPhoto } from '../utils/shrinkPhoto.js';

// Storingen: report a fault (with photo); see your own reports; accommodatiezaken
// handles all of them. Before reporting, the open reports at the chosen location are
// shown so the same fault is followed ("Ik heb dit ook") rather than reported again.
// /storingen/:id opens one report with its timeline (and lets its reporter withdraw it).
// A club housed at a sportcomplex also reports at the complex's places; those reports go
// to the complex's staff, whose list holds the reports of all its clubs.

const ERRORS = {
  title_required: 'Geef een korte omschrijving van de storing.', invalid_location: 'Kies een locatie.',
  invalid_urgency: 'Kies hoe dringend het is.', photo_type: 'De foto moet een afbeelding zijn (jpg, png, webp of gif).',
  photo_too_large: 'De foto is te groot (max. 10 MB).', invalid_description: 'De toelichting is te lang.',
  report_closed: 'Deze melding is al afgehandeld.', own_report: 'Dit is je eigen melding.',
  invalid_duplicate: 'Kies de melding waar dit een dubbele van is.', invalid_owner: 'Kies een actief lid voor de taak.',
  invalid_deadline: 'De deadline klopt niet.', task_exists: 'Er is al een taak voor deze melding.',
  name_required: 'Geef de locatie een naam.', invalid_categories: 'Kies rollen van deze club.', forbidden: 'Dat mag je niet.',
  invalid_note: 'De notitie is te lang.', invalid_status: 'Kies een status.',
  has_followers: 'Anderen hebben dit ook gemeld, dus intrekken kan niet meer.', not_found: 'Deze melding bestaat niet of je mag hem niet zien.',
  message_required: 'Schrijf een bericht of kies een foto.', invalid_message: 'Het bericht is te lang (max. 2000 tekens).',
  no_handlers: 'Er is nog niemand ingesteld om storingen te behandelen. Vraag een clubbeheerder om dat bij Storingen › Instellingen te regelen.',
};
const message = err => ERRORS[err.code] || translateError(err.code) || 'Dat lukte niet.';

const STATUS = {
  nieuw: { label: 'Nieuw', color: '#2563eb', bg: '#dbeafe' },
  in_behandeling: { label: 'In behandeling', color: '#d97706', bg: '#fef3c7' },
  opgelost: { label: 'Opgelost', color: '#16a34a', bg: '#dcfce7' },
  dubbel: { label: 'Dubbel', color: '#6b7280', bg: '#f3f4f6' },
};
const URGENCY = {
  normaal: { label: 'Normaal', hint: 'Kan in de gewone planning' },
  dringend: { label: 'Dringend', hint: 'Hindert trainingen of wedstrijden' },
  gevaarlijk: { label: 'Gevaarlijk', hint: 'Kans op letsel — meteen actie nodig' },
};
const asDate = v => new Date(`${v.replace(' ', 'T')}${v.includes('T') ? '' : 'Z'}`);
const fmtDate = v => (v ? asDate(v).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) : '');
const fmtDateTime = v => (v ? asDate(v).toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
const OPEN = ['nieuw', 'in_behandeling'];
// Who handles a report: the club's accommodatiezaken, or the staff of the complex
const handlerOf = r => (r?.complex_name ? `de beheerder van ${r.complex_name}` : 'accommodatiezaken');
const capital = text => text[0].toUpperCase() + text.slice(1);
const messageCount = n => (n > 0 ? `${n} ${n === 1 ? 'bericht' : 'berichten'}` : null);

function Pill({ status }) {
  const s = STATUS[status] || STATUS.nieuw;
  return <span style={`font-size:11px;font-weight:600;padding:2px 8px;border-radius:10px;background:${s.bg};color:${s.color};white-space:nowrap`}>{s.label}</span>;
}
function UrgencyTag({ urgency }) {
  if (urgency === 'normaal') return null;
  return <span style={`font-size:11px;font-weight:600;padding:2px 8px;border-radius:10px;white-space:nowrap;${urgency === 'gevaarlijk' ? 'background:#fee2e2;color:#dc2626' : 'background:#ffedd5;color:#c2410c'}`}>{URGENCY[urgency].label}</span>;
}

// ── Melden ──────────────────────────────────────────────────────────────────
// "Weer kapot? Meld het opnieuw": the solved report's location and title, taken once
let reportAgain = null;

function ReportForm({ onDone }) {
  const [pre] = useState(() => { const p = reportAgain; reportAgain = null; return p; });
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState(pre?.location_id ? String(pre.location_id) : '');
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState({ title: pre?.title || '', description: '', urgency: 'normaal' });
  const [photo, setPhoto] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const complexName = locations.find(l => l.complex_id)?.complex_name;
  useEffect(() => {
    api('/api/facilities/locations').then(list => {
      setLocations(list);
      // A location that was switched off since can't be chosen any more
      setLocationId(id => (id && !list.some(l => String(l.id) === id) ? '' : id));
    }).catch(err => setError(message(err)));
  }, []);
  const loadOpen = id => api(`/api/facilities/open?location_id=${id}`).then(setOpen).catch(() => setOpen([]));
  useEffect(() => { setOpen(null); if (locationId) loadOpen(locationId); }, [locationId]);

  async function follow(r) {
    setError(null);
    try { await api(`/api/facilities/reports/${r.id}/follow`, { method: 'POST' }); setDone(`Je volgt nu "${r.title}". Je hoort het als het is opgelost.`); loadOpen(locationId); onDone(); }
    catch (err) { setError(message(err)); }
  }
  async function submit() {
    setBusy(true); setError(null);
    const fd = new FormData();
    fd.append('location_id', locationId);
    for (const [k, v] of Object.entries(form)) fd.append(k, v);
    try {
      if (photo) fd.append('photo', await shrinkPhoto(photo));
      await api('/api/facilities/reports', { method: 'POST', body: fd, headers: {} });
      setDone(`Bedankt! ${capital(handlerOf(locations.find(l => String(l.id) === String(locationId))))} heeft je melding ontvangen. Je ziet de status onder "Mijn meldingen".`);
      setForm({ title: '', description: '', urgency: 'normaal' }); setPhoto(null); setLocationId('');
      onDone();
    } catch (err) { setError(message(err)); }
    setBusy(false);
  }

  return (
    <div style="display:flex;flex-direction:column;gap:12px">
      {done && <div class="alert" style="background:#dcfce7;color:#166534;border-radius:8px;padding:10px 12px;font-size:14px">{done}</div>}
      <label class="form-label">Waar is het?
        <select class="form-input" value={locationId} onChange={e => { setLocationId(e.target.value); setDone(null); }}>
          <option value="">Kies een locatie…</option>
          {locations.filter(l => !l.complex_id).map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          {/* The complex's places: their reports go to the complex, not to accommodatiezaken */}
          {complexName && (
            <optgroup label={complexName}>
              {locations.filter(l => l.complex_id).map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </optgroup>
          )}
        </select>
      </label>
      {complexName && <div class="text-sm text-muted" style="margin-top:-6px">Velden, kleedkamers en andere plekken van {complexName} gaan naar de beheerder van het complex.{locations.some(l => !l.complex_id) && ' De rest gaat naar accommodatiezaken.'}</div>}
      {locationId && open === null && <div class="text-sm text-muted">Even kijken wat hier al gemeld is…</div>}
      {locationId && open?.length > 0 && (
        <div style="padding:10px 12px;border:1px solid var(--color-border);border-radius:8px;background:var(--color-bg);display:flex;flex-direction:column;gap:6px">
          <div style="font-size:13px;font-weight:600">Al gemeld op deze plek — is het een van deze?</div>
          {open.map(r => (
            <div key={r.id} style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;font-size:13px">
              <span style="flex:1 1 180px;min-width:0">{r.title} <span class="text-muted">· {r.report_count}× gemeld · {fmtDate(r.created_at)}</span></span>
              <Pill status={r.status} />
              {r.mine ? <span class="text-sm text-muted">Je volgt deze</span> : <Button size="sm" variant="secondary" onClick={() => follow(r)}>Ik heb dit ook</Button>}
            </div>
          ))}
          <div class="text-sm text-muted">Staat het er niet tussen? Meld het hieronder.</div>
        </div>
      )}
      {locationId && open !== null && (
        <>
          <label class="form-label">Wat is er aan de hand?
            <input class="form-input" maxLength={150} value={form.title} onInput={e => set('title', e.target.value)} placeholder="Douche kleedkamer 3 blijft koud" />
          </label>
          <label class="form-label">Toelichting (optioneel)
            <textarea class="form-input" rows={3} maxLength={2000} value={form.description} onInput={e => set('description', e.target.value)} placeholder="Sinds wanneer, wat heb je al geprobeerd?" />
          </label>
          <AiAssist kind="facility_description" value={form.description} onUse={text => set('description', text)}
            context={{ title: form.title, location: locations.find(l => String(l.id) === String(locationId))?.name || '' }} />
          <div class="form-label">Hoe dringend?
            <div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:4px">
              {Object.entries(URGENCY).map(([k, u]) => (
                <label key={k} style={`flex:1 1 140px;display:flex;gap:6px;align-items:flex-start;padding:8px;border:1px solid ${form.urgency === k ? 'var(--color-primary)' : 'var(--color-border)'};border-radius:8px;cursor:pointer;font-weight:400`}>
                  <input type="radio" name="urgency" checked={form.urgency === k} onChange={() => set('urgency', k)} />
                  <span><strong style="font-size:13px">{u.label}</strong><br /><span class="text-sm text-muted">{u.hint}</span></span>
                </label>
              ))}
            </div>
          </div>
          <label class="form-label">Foto (optioneel)
            <input class="form-input" type="file" accept="image/*" onChange={e => setPhoto(e.target.files?.[0] || null)} />
          </label>
          {error && <div class="alert alert-error">{error}</div>}
          <div><Button onClick={submit} disabled={busy || !form.title.trim()}>{busy ? 'Versturen…' : 'Storing melden'}</Button></div>
        </>
      )}
      {!locationId && error && <div class="alert alert-error">{error}</div>}
    </div>
  );
}

// ── Mijn meldingen ──────────────────────────────────────────────────────────
function MyReports({ reload }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api('/api/facilities/reports/mine').then(setItems).catch(() => setItems([])); }, [reload]);
  if (!items) return <div class="text-muted">Laden…</div>;
  if (!items.length) return <div class="text-muted text-sm">Je hebt nog niets gemeld.</div>;
  return (
    <div style="display:flex;flex-direction:column;gap:8px">
      {items.map(r => (
        <a key={r.id} href={`/storingen/${r.id}`} class="facility-report-link">
          <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap">
            <span style="font-weight:600;flex:1 1 180px;min-width:0">{r.title}</span>
            <UrgencyTag urgency={r.urgency} />
            <Pill status={r.status} />
            <span aria-hidden="true" class="text-muted">›</span>
          </div>
          <div class="text-sm text-muted">
            {[r.location_name, r.complex_name, fmtDate(r.created_at), r.report_count > 1 && `${r.report_count}× gemeld`, !r.own && 'je volgt deze', r.withdrawn && 'ingetrokken', messageCount(r.message_count), r.status === 'dubbel' && r.duplicate_of && `zelfde als melding #${r.duplicate_of}`].filter(Boolean).join(' · ')}
          </div>
          {r.note && <div class="text-sm" style="font-style:italic">“{r.note}”</div>}
        </a>
      ))}
    </div>
  );
}

// ── Eén melding: alles erover en het verloop ────────────────────────────────
function eventText(e, role) {
  const by = e.mine ? ' door jou' : e.user_name ? ` door ${e.user_name}` : '';
  switch (e.kind) {
    case 'gemeld': return `Gemeld${by}`;
    case 'taak': return `Taak aangemaakt, in behandeling${by}`;
    case 'taak_afgerond': return `Opgelost: taak afgerond${by}`;
    case 'taak_heropend': return 'Taak heropend: weer in behandeling';
    case 'ingetrokken': return e.mine ? 'Ingetrokken door jou: het probleem is weg' : `Ingetrokken door ${role === 'handler' && e.user_name ? e.user_name : 'de melder'}: het probleem is weg`;
    default: return `${{ nieuw: 'Teruggezet naar Nieuw', in_behandeling: 'In behandeling', opgelost: 'Opgelost', dubbel: 'Dubbel van een andere melding' }[e.status] || 'Bijgewerkt'}${by}`;
  }
}

export function ReportDetail({ id }) {
  const [r, setR] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  // The newest successful answer for the report on screen wins: a slower older one, or
  // one for the report we just left, doesn't overwrite it; a failed newer one doesn't
  // throw away an older one that did arrive
  const idNow = useRef(id);
  idNow.current = id;
  const requests = useRef(0);
  const shown = useRef({ nr: 0, id: null });
  const load = () => {
    const nr = ++requests.current;
    const forId = id;
    return api(`/api/facilities/reports/${forId}`).then(d => {
      if (forId !== idNow.current || nr <= shown.current.nr) return;
      shown.current = { nr, id: forId };
      setError(null); // it loaded after all
      setR(d);
    });
  };
  useEffect(() => {
    setR(null); setError(null);
    shown.current = { nr: requests.current, id: null }; // answers asked before this switch don't count
    load().catch(err => { if (id === idNow.current && shown.current.id !== id) setError(message(err)); });
  }, [id]);
  const reload = () => load();
  const back = () => route(`/storingen?tab=${listOf(r)}`);

  // Notifications about this report come in live (a message, solved…): show them
  const list = notifications.value;
  const seen = useRef(list[0]?.id);
  useEffect(() => {
    if (!list[0] || list[0].id === seen.current) return;
    const fresh = newSince(list, seen.current);
    seen.current = list[0].id;
    if (fresh.some(n => aboutReport(n, id))) reload().catch(() => {});
  }, [list[0]?.id]);

  async function act(fn, done) {
    setBusy(true); setError(null);
    try { await fn(); await done(); } catch (err) { setError(message(err)); }
    setBusy(false);
  }
  const withdraw = () => confirm(`Is het probleem weg? Dan trek je je melding in en hoort ${handlerOf(r)} het.`)
    && act(() => api(`/api/facilities/reports/${id}/withdraw`, { method: 'POST' }), reload);
  const unfollow = () => confirm(`"${r.title}" niet meer volgen?`)
    && act(() => api(`/api/facilities/reports/${id}/follow`, { method: 'DELETE' }), async () => back());

  if (!r) {
    return error
      ? <div style="display:flex;flex-direction:column;gap:10px;align-items:flex-start"><div class="alert alert-error">{error}</div><Button variant="secondary" size="sm" onClick={() => route('/storingen?tab=mijn')}>← Mijn meldingen</Button></div>
      : <div class="text-muted">Laden…</div>;
  }
  const reportAgainFrom = () => { reportAgain = { location_id: r.location_id, title: r.title }; route('/storingen?tab=melden'); };
  return <ReportBody r={r} busy={busy} error={error} onBack={back} onWithdraw={withdraw} onUnfollow={unfollow} onSent={reload} onReportAgain={reportAgainFrom} />;
}

// A message to the other side: text, a photo (made smaller first, as with a report) or both
function MessageForm({ reportId, toReporter, handler, onSent }) {
  const [body, setBody] = useState('');
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [inputKey, setInputKey] = useState(0); // a new file input after sending: the old one keeps its file
  async function send() {
    setBusy(true); setError(null);
    const fd = new FormData();
    fd.append('body', body);
    try {
      if (photo) fd.append('photo', await shrinkPhoto(photo));
      await api(`/api/facilities/reports/${reportId}/messages`, { method: 'POST', body: fd, headers: {} });
    } catch (err) { setError(message(err)); setBusy(false); return; }
    setBody(''); setPhoto(null); setInputKey(k => k + 1); setBusy(false);
    // Sent: a failing reload is no reason to say it didn't work
    onSent?.()?.catch?.(() => {});
  }
  return (
    <div style="display:flex;flex-direction:column;gap:8px">
      <textarea class="form-input" rows={2} maxLength={2000} value={body} onInput={e => setBody(e.target.value)} aria-label="Bericht"
        placeholder={toReporter ? 'Vraag of laat de melder iets weten…' : 'Vul je melding aan of stel een vraag…'} />
      <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap">
        <input key={inputKey} class="form-input" style="flex:1 1 180px;min-width:0" type="file" accept="image/*" aria-label="Foto toevoegen" onChange={e => setPhoto(e.target.files?.[0] || null)} />
        <Button size="sm" disabled={busy || (!body.trim() && !photo)} onClick={send}>{busy ? 'Versturen…' : 'Versturen'}</Button>
      </div>
      <div class="text-sm text-muted">{toReporter ? 'De melder krijgt een seintje.' : `${capital(handler)} krijgt een seintje.`}</div>
      {error && <div class="alert alert-error">{error}</div>}
    </div>
  );
}

// Timeline and messages in one list, oldest first
const timeline = r => [
  ...r.events.map(e => ({ ...e, type: 'event' })),
  ...(r.messages || []).map(m => ({ ...m, type: 'message' })),
].sort((a, b) => a.created_at.localeCompare(b.created_at) || (a.type === b.type ? a.id - b.id : a.type === 'event' ? -1 : 1));

export const aboutReport = (n, id) => n.reference_type === 'facility_report' && Number(n.reference_id) === Number(id);
// The notifications that came in after the one seen last (newest first; two may arrive
// before the screen catches up)
export function newSince(list, seenId) {
  const i = list.findIndex(n => n.id === seenId);
  return i === -1 ? list : list.slice(0, i);
}

// Back to the list the report is on: Mijn meldingen for its reporter and followers
// (also a handler who said "Ik heb dit ook"), else Alle meldingen
const listOf = r => (r && !r.own && !r.following && r.role === 'handler' ? 'alle' : 'mijn');
const mine = r => r.own || r.following;

export function ReportBody({ r, busy, error, onBack, onWithdraw, onUnfollow, onSent, onReportAgain }) {
  const open = OPEN.includes(r.status);
  return (
    <div style="display:flex;flex-direction:column;gap:14px;min-width:0">
      <div><Button variant="secondary" size="sm" onClick={onBack}>← {listOf(r) === 'alle' ? 'Alle meldingen' : 'Mijn meldingen'}</Button></div>
      <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap">
        <h2 style="font-size:18px;font-weight:600;margin:0;flex:1 1 220px;min-width:0;overflow-wrap:anywhere">{r.role === 'handler' && `#${r.id} `}{r.title}</h2>
        <UrgencyTag urgency={r.urgency} />
        <Pill status={r.status} />
      </div>
      <div class="text-sm text-muted">
        {[r.club_name, r.location_name, r.role !== 'handler' && r.complex_name, `gemeld ${fmtDate(r.created_at)}`, r.reported_by_name && `door ${r.reported_by_name}`, r.report_count > 1 && `${r.report_count}× gemeld`, r.following && !r.own && 'je volgt deze', r.handled_by_name && `behandeld door ${r.handled_by_name}`].filter(Boolean).join(' · ')}
      </div>
      {r.via_form && r.role === 'handler' && (
        <div class="text-sm" style="padding:8px 10px;border-radius:8px;background:var(--color-bg)">
          Gemeld via het meldformulier{r.guest_email ? <> · <a href={`mailto:${r.guest_email}`}>{r.guest_email}</a></> : ''}. Een bericht hieronder gaat per mail naar de melder.
        </div>
      )}
      {r.description && <div style="white-space:pre-line;overflow-wrap:anywhere">{r.description}</div>}
      {(r.has_photo || r.task_id) && (
        <div style="display:flex;gap:12px;flex-wrap:wrap" class="text-sm">
          {r.has_photo && <PhotoLink src={`/api/facilities/reports/${r.id}/photo`} alt={r.title}>📷 Foto bekijken</PhotoLink>}
          {r.task_id && <a href={`/tasks/${r.task_id}`}>Taak bekijken</a>}
        </div>
      )}
      {r.status === 'dubbel' && r.duplicate_of && <div class="text-sm">Dit is dezelfde storing als <a href={`/storingen/${r.duplicate_of}`}>melding #{r.duplicate_of}</a>.{mine(r) && ' Je volgt die nu.'}</div>}
      {r.note && (
        <div style="padding:10px 12px;border-radius:8px;background:var(--color-bg)" class="text-sm">
          <div style="font-weight:600;margin-bottom:2px">{r.role === 'handler' ? 'Notitie' : `Bericht van ${handlerOf(r)}`}</div>
          <div style="white-space:pre-line;overflow-wrap:anywhere">{r.note}</div>
        </div>
      )}

      <div>
        <div style="font-weight:600;margin-bottom:6px">{r.messages?.length ? 'Verloop en berichten' : 'Verloop'}</div>
        <ol class="facility-timeline">
          {timeline(r).map(item => (item.type === 'event'
            ? (
              <li key={`e${item.id}`}>
                <span class="text-sm">{eventText(item, r.role)}</span>
                <span class="text-sm text-muted">{fmtDateTime(item.created_at)}</span>
              </li>
            ) : (
              <li key={`m${item.id}`} class={`facility-message${item.mine ? ' facility-message-mine' : ''}`}>
                <span class="text-sm" style="font-weight:600">{item.mine ? 'Jij' : item.user_name || 'Verwijderd lid'}</span>
                {item.body && <span style="white-space:pre-line;overflow-wrap:anywhere">{item.body}</span>}
                {item.has_photo && <span class="text-sm"><PhotoLink src={`/api/facilities/reports/${r.id}/messages/${item.id}/photo`} alt="Foto bij bericht">📷 Foto bekijken</PhotoLink></span>}
                <span class="text-sm text-muted">{fmtDateTime(item.created_at)}</span>
              </li>
            )))}
        </ol>
      </div>

      {r.can_message && <MessageForm reportId={r.id} toReporter={r.role === 'handler' && !r.own} handler={handlerOf(r)} onSent={onSent} />}
      {mine(r) && r.status === 'opgelost' && (
        <div class="text-sm" style="display:flex;gap:8px;align-items:center;flex-wrap:wrap">
          <span class="text-muted">Weer kapot?</span>
          <Button variant="secondary" size="sm" onClick={onReportAgain}>Meld het opnieuw</Button>
        </div>
      )}

      {r.own && open && (r.can_withdraw
        ? <div><Button variant="secondary" size="sm" disabled={busy} onClick={onWithdraw}>Melding intrekken</Button><div class="text-sm text-muted" style="margin-top:4px">Is het probleem vanzelf weg? Trek je melding dan in.</div></div>
        : <div class="text-sm text-muted">Anderen hebben dit ook gemeld, dus intrekken kan niet. Is het opgelost? Dan zet {handlerOf(r)} de melding op opgelost.</div>)}
      {r.following && !r.own && open && <div><Button variant="secondary" size="sm" disabled={busy} onClick={onUnfollow}>Niet meer volgen</Button></div>}
      {r.role === 'handler' && <div class="text-sm"><a href="/storingen?tab=alle">Behandelen bij Alle meldingen</a></div>}
      {error && <div class="alert alert-error">{error}</div>}
    </div>
  );
}

// ── Accommodatiezaken: alle meldingen ───────────────────────────────────────
function HandlerReport({ r, openReports, members, onChanged, canTask }) {
  const [note, setNote] = useState(r.note || '');
  const [dupOf, setDupOf] = useState(r.duplicate_of || '');
  const [taskForm, setTaskForm] = useState(null);
  const [error, setError] = useState(null);
  async function run(fn) { setError(null); try { await fn(); onChanged(); return true; } catch (err) { setError(message(err)); return false; } }
  const patch = body => run(() => api(`/api/facilities/reports/${r.id}`, { method: 'PATCH', body: JSON.stringify(body) }));
  const closed = ['opgelost', 'dubbel'].includes(r.status);
  return (
    <div style={`padding:10px 12px;border:1px solid var(--color-border);border-left:4px solid ${STATUS[r.status].color};border-radius:8px;display:flex;flex-direction:column;gap:6px;min-width:0${closed ? ';opacity:.8' : ''}`}>
      <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap">
        <a href={`/storingen/${r.id}`} style="font-weight:600;flex:1 1 200px;min-width:0;color:inherit">#{r.id} {r.title}</a>
        <UrgencyTag urgency={r.urgency} />
        <Pill status={r.status} />
      </div>
      <div class="text-sm text-muted">
        {[r.via_form && 'via formulier', r.club_name, r.location_name, `gemeld door ${r.reported_by_name || 'onbekend'}`, fmtDate(r.created_at), r.follower_count > 0 && `${r.follower_count + 1}× gemeld`, r.handled_by_name && `behandeld door ${r.handled_by_name}`, messageCount(r.message_count)].filter(Boolean).join(' · ')}
      </div>
      {r.description && <div class="text-sm" style="white-space:pre-line">{r.description}</div>}
      <div style="display:flex;gap:10px;flex-wrap:wrap;align-items:center" class="text-sm">
        {r.has_photo && <PhotoLink src={`/api/facilities/reports/${r.id}/photo`} alt={r.title}>📷 Foto</PhotoLink>}
        {r.task_id && <a href={`/tasks/${r.task_id}`}>Taak bekijken</a>}
      </div>
      <div style="display:flex;gap:6px;flex-wrap:wrap;align-items:center">
        <select class="form-input" style="width:auto;min-width:160px;height:34px;padding:0 8px;font-size:13px" value={r.status} aria-label="Status"
          onChange={e => e.target.value !== 'dubbel' && patch({ status: e.target.value, note })}>
          {['nieuw', 'in_behandeling', 'opgelost'].map(s => <option key={s} value={s}>{STATUS[s].label}</option>)}
          {r.status === 'dubbel' && <option value="dubbel">Dubbel</option>}
        </select>
        {canTask && !r.task_id && !closed && <Button size="sm" variant="secondary" onClick={() => setTaskForm(taskForm ? null : { owner_id: '', deadline: '' })}>Taak maken</Button>}
      </div>
      {taskForm && (
        <div style="display:flex;gap:6px;flex-wrap:wrap;align-items:center">
          <select class="form-input" style="width:auto;height:32px;font-size:13px" value={taskForm.owner_id} onChange={e => setTaskForm(t => ({ ...t, owner_id: e.target.value }))} aria-label="Voor wie">
            <option value="">Voor wie?</option>
            {members.map(m => <option key={m.id} value={m.id}>{m.display_name}</option>)}
          </select>
          <input class="form-input" type="date" style="width:auto;height:32px;font-size:13px" value={taskForm.deadline} onInput={e => setTaskForm(t => ({ ...t, deadline: e.target.value }))} aria-label="Deadline" />
          <Button size="sm" disabled={!taskForm.owner_id} onClick={() => run(() => api(`/api/facilities/reports/${r.id}/task`, { method: 'POST', body: JSON.stringify(taskForm) })).then(ok => ok && setTaskForm(null))}>Aanmaken</Button>
        </div>
      )}
      <div style="display:flex;gap:6px;flex-wrap:wrap;align-items:center">
        <input class="form-input" style="flex:1 1 200px;height:32px;font-size:13px" maxLength={2000} value={note} onInput={e => setNote(e.target.value)} placeholder="Notitie (de melder ziet deze bij 'opgelost')" aria-label="Notitie" />
        <Button size="sm" variant="secondary" disabled={note === (r.note || '')} onClick={() => patch({ status: r.status, note, duplicate_of: r.duplicate_of })}>Notitie opslaan</Button>
      </div>
      {!closed && openReports.some(o => o.id !== r.id) && (
        <div style="display:flex;gap:6px;flex-wrap:wrap;align-items:center">
          <select class="form-input" style="flex:1 1 200px;height:32px;font-size:13px" value={dupOf} onChange={e => setDupOf(e.target.value)} aria-label="Dubbel van">
            <option value="">Dubbel van…</option>
            {openReports.filter(o => o.id !== r.id).map(o => <option key={o.id} value={o.id}>#{o.id} {o.title}</option>)}
          </select>
          <Button size="sm" variant="secondary" disabled={!dupOf} onClick={() => patch({ status: 'dubbel', duplicate_of: Number(dupOf), note })}>Markeer als dubbel</Button>
        </div>
      )}
      {error && <div class="text-sm" style="color:var(--color-danger)">{error}</div>}
    </div>
  );
}

// complex: a sportcomplex's list (no tasks there)
function AllReports({ complex }) {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState('open');
  const [club, setClub] = useState('');
  const [members, setMembers] = useState([]);
  const load = () => api('/api/facilities/reports').then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); api('/api/facilities/members').then(setMembers).catch(() => {}); }, []);
  if (!items) return <div class="text-muted">Laden…</div>;
  const openReports = items.filter(r => ['nieuw', 'in_behandeling'].includes(r.status));
  // A complex's list holds several clubs: show one of them
  const clubs = [...new Set(items.map(r => r.club_name).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'nl'));
  const shown = (filter === 'open' ? openReports : filter === 'alle' ? items : items.filter(r => r.status === filter))
    .filter(r => !club || r.club_name === club);
  return (
    <div style="display:flex;flex-direction:column;gap:10px">
      <div style="display:flex;gap:6px;flex-wrap:wrap">
        {[['open', `Open (${openReports.length})`], ['opgelost', 'Opgelost'], ['dubbel', 'Dubbel'], ['alle', 'Alle']].map(([k, l]) => (
          <Button key={k} size="sm" variant={filter === k ? 'primary' : 'secondary'} onClick={() => setFilter(k)}>{l}</Button>
        ))}
        {clubs.length > 1 && (
          <select class="form-input" style="width:auto;min-width:160px;height:32px;padding:0 8px;font-size:13px" value={club} onChange={e => setClub(e.target.value)} aria-label="Club">
            <option value="">Alle clubs</option>
            {clubs.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
      </div>
      {shown.length === 0 && <div class="text-muted text-sm">Geen meldingen.</div>}
      {shown.map(r => <HandlerReport key={`${r.id}:${r.status}:${r.note || ''}`} r={r} openReports={openReports} members={members} onChanged={load} canTask={!complex} />)}
    </div>
  );
}

// ── Instellingen ────────────────────────────────────────────────────────────
// A sportcomplex: the QR codes of its public meldformulier, for the whole complex and per
// place (a sticker to print, the place filled in)
function ComplexQr({ locations }) {
  const [qr, setQr] = useState(null);
  useEffect(() => { api('/api/complexes/qr').then(setQr).catch(() => setQr(null)); }, []);
  if (!qr) return null;
  const sticker = id => `/api/complexes/qr/sticker${id ? `?location_id=${id}` : ''}`;
  return (
    <div>
      <div style="font-weight:600;margin-bottom:4px">Meldformulier met QR-code</div>
      <div class="text-sm text-muted" style="margin-bottom:8px">Iedereen op het complex meldt hiermee een storing, ook zonder Vereniging4U: met naam en e-mail, en updates per mail.</div>
      <div style="display:flex;gap:12px;align-items:center;flex-wrap:wrap">
        <span style="width:96px;height:96px;display:inline-block;background:#fff;border-radius:8px" dangerouslySetInnerHTML={{ __html: qr.svg }} />
        <span style="flex:1 1 200px;min-width:0;font-size:13px;overflow-wrap:anywhere"><a href={qr.url} target="_blank" rel="noopener noreferrer">{qr.url}</a><br />
          <a href={sticker(null)} target="_blank" rel="noopener noreferrer">Printen voor het hele complex</a></span>
      </div>
      <div class="text-sm" style="margin-top:8px">Per plek (de plek staat dan al ingevuld):{' '}
        {locations.filter(l => l.is_active).map((l, i) => <span key={l.id}>{i ? ' · ' : ''}<a href={sticker(l.id)} target="_blank" rel="noopener noreferrer">{l.name}</a></span>)}
      </div>
    </div>
  );
}

function Settings({ access }) {
  const [locations, setLocations] = useState([]);
  const [newName, setNewName] = useState('');
  const [roles, setRoles] = useState(null);
  const [error, setError] = useState(null);
  const loadLocations = () => api('/api/facilities/locations/all').then(setLocations).catch(() => {});
  useEffect(() => {
    if (access.handle) loadLocations();
    if (access.manage_handlers) api('/api/facilities/handlers').then(setRoles).catch(() => {});
  }, []);
  async function run(fn) { setError(null); try { await fn(); } catch (err) { setError(message(err)); } }
  return (
    <div style="display:flex;flex-direction:column;gap:16px">
      {access.handle && (
        <div>
          <div style="font-weight:600;margin-bottom:6px">Locaties</div>
          {locations.map(l => (
            <div key={l.id} style="display:flex;gap:8px;align-items:center;padding:4px 0;font-size:14px">
              <span style={`flex:1;${l.is_active ? '' : 'color:var(--color-text-muted);text-decoration:line-through'}`}>{l.name}</span>
              <Button size="sm" variant="secondary" onClick={() => run(async () => { await api(`/api/facilities/locations/${l.id}`, { method: 'PUT', body: JSON.stringify({ name: l.name, is_active: !l.is_active }) }); loadLocations(); })}>
                {l.is_active ? 'Uitzetten' : 'Aanzetten'}
              </Button>
            </div>
          ))}
          <div style="display:flex;gap:6px;margin-top:6px">
            <input class="form-input" style="flex:1;height:32px" maxLength={100} value={newName} onInput={e => setNewName(e.target.value)} placeholder="Nieuwe locatie, bijv. Veld 3" aria-label="Nieuwe locatie" />
            <Button size="sm" disabled={!newName.trim()} onClick={() => run(async () => { await api('/api/facilities/locations', { method: 'POST', body: JSON.stringify({ name: newName }) }); setNewName(''); loadLocations(); })}>Toevoegen</Button>
          </div>
        </div>
      )}
      {access.complex && access.handle && <ComplexQr locations={locations} />}
      {access.manage_handlers && roles && (
        <div>
          <div style="font-weight:600;margin-bottom:4px">Wie behandelt de meldingen (accommodatiezaken)</div>
          <div class="text-sm text-muted" style="margin-bottom:6px">Leden met een van deze rollen zien alle meldingen en krijgen een bericht bij een nieuwe storing.</div>
          <div style="display:flex;flex-direction:column;gap:2px;max-height:320px;overflow:auto;border:1px solid var(--color-border);border-radius:8px;padding:6px 10px">
            {roles.map(c => (
              <label key={c.id} style="display:flex;gap:8px;align-items:center;font-size:13px;font-weight:400">
                <input type="checkbox" checked={c.selected} onChange={e => setRoles(rs => rs.map(x => (x.id === c.id ? { ...x, selected: e.target.checked } : x)))} />
                <span>{c.name} <span class="text-muted">· {c.group_name}</span></span>
              </label>
            ))}
          </div>
          <div style="margin-top:6px"><Button size="sm" onClick={() => run(async () => setRoles(await api('/api/facilities/handlers', { method: 'PUT', body: JSON.stringify({ category_ids: roles.filter(r => r.selected).map(r => r.id) }) })))}>Opslaan</Button></div>
        </div>
      )}
      {error && <div class="alert alert-error">{error}</div>}
    </div>
  );
}

const TABS = ['melden', 'mijn', 'alle', 'instellingen'];
const tabInUrl = complex => {
  const t = new URLSearchParams(window.location.search).get('tab');
  // A complex's staff mostly handle: they start on the list
  return TABS.includes(t) ? t : complex ? 'alle' : 'melden';
};

export default function StoringenView({ id, url }) {
  const [access, setAccess] = useState(null);
  // ?tab= : back from a report lands on the list it came from. Read again on every
  // route: the router keeps this component between /storingen/:id and /storingen.
  const [tab, setTab] = useState(() => tabInUrl(false));
  useEffect(() => { if (!id) setTab(tabInUrl(access?.complex)); }, [id, url, access?.complex]);
  const [reload, setReload] = useState(0);
  useEffect(() => { api('/api/facilities/access').then(setAccess).catch(() => setAccess({ report: false })); }, []);
  if (!access) return <div class="text-muted" style="padding:40px;text-align:center">Laden…</div>;
  if (!access.report && !access.handle) return <div class="alert alert-error">Je hebt geen toegang tot storingsmeldingen.</div>;
  // access.complex_only: Storingen outside the plan, housed at a complex (only the complex's places)
  const tabs = [['melden', 'Storing melden'], ['mijn', 'Mijn meldingen'], access.handle && ['alle', 'Alle meldingen'], (access.handle || access.manage_handlers) && ['instellingen', 'Instellingen']].filter(Boolean);
  return (
    <div>
      <div class="page-header" style="margin-bottom:16px">
        <h1 class="page-title">🔧 Storingen</h1>
      </div>
      {!id && (
        <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:16px">
          {tabs.map(([k, l]) => <Button key={k} variant={tab === k ? 'primary' : 'secondary'} size="sm" onClick={() => setTab(k)}>{l}</Button>)}
        </div>
      )}
      {!id && access.complex_only && (
        <div class="text-sm text-muted" style="margin-bottom:12px">Met jullie pakket meld je hier storingen op de plekken van het sportcomplex. Eigen plekken en afhandeling door jullie eigen accommodatiezaken zitten in Plus.</div>
      )}
      {!id && !access.has_handlers && (
        <div class="alert" style="background:#fef3c7;color:#92400e;border-radius:8px;padding:10px 12px;font-size:14px;margin-bottom:12px">
          {access.manage_handlers
            ? 'Er is nog niemand ingesteld om storingen te behandelen. Kies bij Instellingen welke rollen dat doen; tot dan kan niemand melden.'
            : 'Storingen melden kan nog niet: er is nog niemand ingesteld om ze te behandelen.'}
        </div>
      )}
      <Card>
        {id && <ReportDetail id={id} />}
        {!id && tab === 'melden' && <ReportForm onDone={() => setReload(n => n + 1)} />}
        {!id && tab === 'mijn' && <MyReports reload={reload} />}
        {!id && tab === 'alle' && access.handle && <AllReports complex={access.complex} />}
        {!id && tab === 'instellingen' && <Settings access={access} />}
      </Card>
    </div>
  );
}
